import { Navigate, Outlet, useMatch } from 'react-router-dom'

import { AllFeatureIds } from 'generated/featureIds.generated'
import { PayrollRootPageDocument } from 'generated/resources/graphql'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useModuleAvailabilities } from 'modules/core/hooks/useModuleAvailabilities'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'

import subscriptionCollections from 'modules/billing/store/collections/subscriptionAddons'
import CollectionsBootstrap from 'modules/payroll_bundle/lib/CollectionsBootstrap'
import { PayrollProvider } from 'modules/payroll_bundle/lib/payrollContext'
import { PolicyPeriodsProvider } from 'modules/payroll_bundle/lib/policyPeriodsContext'
import policies, { PoliciesProvider } from 'modules/payroll_bundle/store/collections/policies'
import policyPeriods from 'modules/payroll_bundle/store/collections/policyPeriods'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import PayrollRestrictionPage from './RestrictionPage'

const payrollBundleFF: AllFeatureIds = 'payroll_bundle'
const newPayrollsetupFF: AllFeatureIds = 'dev_payroll_new_setup'
const newPayrollSetupCompleteFF: AllFeatureIds = 'payroll_setup_complete'

const Container = () => {
  const { navigation } = useNavigation()

  const policyPeriodCollection = useLazyRef(() => policyPeriods).current
  const { loading: loadingModuleAvailabilities } = useModuleAvailabilities()

  const { company } = useCurrentSession()
  const { data } = useQuery(PayrollRootPageDocument, {
    variables: {
      companyId: company.id,
    },
  })

  const { loading: policiesLoading } = useAsyncData(async () => {
    await Promise.all([
      subscriptionCollections.fetch(),
      policyAuthorizations.authorizePolicies('payroll_supplements.supplements.create', []),
      policyAuthorizations.authorizePolicies('payroll_supplements.supplements.read', []),
      policyAuthorizations.authorizePolicies('payroll_supplements.observations.read', []),
    ])
  }, [])

  const isSetupPage = useMatch(navigation.payrollBundle.setup.fullPath())

  if (policiesLoading || loadingModuleAvailabilities || !data) return null

  const features: Record<string, boolean> = data.features.featuresConnection.edges
    .map((f) => f.node)
    .reduce(
      (acc, feature) => ({
        ...acc,
        [feature.name]: feature.enabled,
      }),
      {}
    )

  const canSeePayroll = data.payroll._policies.sidebarSeePayrollBundleTopLevelItem.can

  if (canSeePayroll) {
    const hasNewPayrollSetup = features[newPayrollsetupFF]
    const isPayrollSetupComplete = hasNewPayrollSetup
      ? features[newPayrollSetupCompleteFF]
      : features[payrollBundleFF]

    if (!isSetupPage && !isPayrollSetupComplete) {
      return <Navigate to={navigation.payrollBundle.setup.fullPath()} />
    }

    return (
      <PayrollProvider>
        <PoliciesProvider collection={policies}>
          <PolicyPeriodsProvider policyPeriodCollection={policyPeriodCollection}>
            <Outlet />
          </PolicyPeriodsProvider>
        </PoliciesProvider>
      </PayrollProvider>
    )
  }

  return <PayrollRestrictionPage />
}

const RootPage = () => {
  return (
    <CollectionsBootstrap>
      <Container />
    </CollectionsBootstrap>
  )
}

export default RootPage
